import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtDateTime } from "../lib/format";
import { IconAlertTriangle, IconTruck } from "./icons";
import type { VehicleKmTimelineDto, KmSegmentType } from "@bi/types";

/**
 * Memória do card "KM Rodado": a linha do tempo operacional da placa.
 *
 * Mostra os trechos na ordem em que aconteceram — incluindo os VAZIOS entre o
 * destino de uma viagem e a origem da próxima, que é onde some o KM que ninguém
 * consegue explicar — e concilia a soma deles com o KM apurado no abastecimento.
 *
 * O que não foi explicado aparece como número próprio e NÃO é rateado entre as
 * rotas: distribuir o resto daria um total redondo e mentiroso.
 */

type TypeFilter = "all" | "freight" | "empty";

const TYPE_STYLE: Record<KmSegmentType, { dot: string; text: string }> = {
  FREIGHT: { dot: "bg-tm-500", text: "text-gray-900" },
  REPOSITIONING: { dot: "bg-amber-400", text: "text-amber-700" },
  RETURN_TO_BASE: { dot: "bg-blue-400", text: "text-blue-700" },
  MAINTENANCE_TRANSFER: { dot: "bg-purple-400", text: "text-purple-700" },
  UNIDENTIFIED: { dot: "bg-gray-300", text: "text-gray-500" },
};

const km = (v: number | null) => (v == null ? "—" : `${fmtNumber(Math.round(v))} km`);

/** Uma parcela da conciliação. `strong` destaca o que a leitura precisa notar. */
function ReconCell({ label, value, hint, strong }: {
  label: string; value: string; hint?: string; strong?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`mt-0.5 ${strong ? "text-lg font-semibold text-amber-700" : "text-lg font-semibold text-gray-900"}`}>
        {value}
      </p>
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

export function KmTimelinePanel({ plate, from, to, onClose }: {
  plate: string; from: string; to: string; onClose: () => void;
}) {
  const [filter, setFilter] = useState<TypeFilter>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["transport", "km-timeline", plate, from, to],
    queryFn: () =>
      api.get<VehicleKmTimelineDto>(
        `/dashboards/transport/vehicle/${encodeURIComponent(plate)}/km-timeline?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ).catch(() => null),
    staleTime: 60_000,
    enabled: plate.length >= 5,
  });

  const segments = data?.segments ?? [];
  const freightCount = segments.filter((s) => s.type === "FREIGHT").length;
  const emptyCount = segments.filter((s) => s.type === "REPOSITIONING").length;
  const shown = segments.filter((s) =>
    filter === "all" ? true : filter === "freight" ? s.type === "FREIGHT" : s.type === "REPOSITIONING",
  );

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-surface-border flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Linha do tempo operacional</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {isLoading ? "carregando…"
              : !data ? "não foi possível carregar"
              // A âncora só é mencionada quando existe trecho: dizer "ancorada na
              // viagem anterior" numa linha do tempo vazia sugere que algo foi
              // apurado quando nada foi.
              : `${data.tripCount} viagem(ns) · ${segments.length} trecho(s)` +
                (data.previousTripId != null && segments.length > 0
                  ? " · ancorada na viagem anterior ao período" : "")}
          </p>
        </div>
        <button className="text-xs text-gray-400 hover:text-gray-700 shrink-0" onClick={onClose}>fechar ✕</button>
      </div>

      {isLoading ? (
        <p className="px-5 py-6 text-sm text-gray-400">Carregando linha do tempo…</p>
      ) : !data ? (
        <p className="px-5 py-6 text-sm text-gray-400 text-center">Não foi possível carregar a linha do tempo.</p>
      ) : (
        <>
          {/* Conciliação: a soma dos trechos vs. o KM que o card mostra */}
          <div className="px-5 py-4 border-b border-surface-border bg-gray-50/60">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <ReconCell label="KM apurado" value={km(data.reconciliation.actualTotalKm)} hint="hodômetro do abastecimento" />
              <ReconCell label="Em fretes" value={km(data.reconciliation.freightKm)} />
              <ReconCell
                label="Vazio (reposicionamento)"
                value={km(data.reconciliation.repositioningKm)}
                hint={
                  data.reconciliation.emptyPercent != null
                    // Denominador explícito: é sobre o KM EXPLICADO, não sobre o apurado.
                    // Sem o rótulo, 12% seria lido como "rodou 12% do mês vazio".
                    ? `${data.reconciliation.emptyPercent.toString().replace(".", ",")}% do KM explicado`
                    : undefined
                }
              />
              <ReconCell label="Explicado" value={km(data.reconciliation.explainedKm)} />
              <ReconCell
                label="Não conciliado"
                value={km(data.reconciliation.unreconciledKm)}
                hint="não é rateado entre as rotas"
                strong={data.reconciliation.unreconciledKm > 0}
              />
            </div>
          </div>

          {/* Filtro por tipo de trecho */}
          {segments.length > 0 && (
            <div className="px-5 py-3 border-b border-surface-border flex flex-wrap gap-2">
              {([
                ["all", `Todos (${segments.length})`],
                ["freight", `Fretes (${freightCount})`],
                ["empty", `Vazios (${emptyCount})`],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  aria-pressed={filter === key}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    filter === key
                      ? "bg-tm-600 border-tm-600 text-white"
                      : "bg-white border-surface-border text-gray-600 hover:border-tm-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {segments.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <IconTruck className="w-7 h-7 mx-auto text-gray-300" />
              <p className="text-sm text-gray-500 mt-2">Sem trechos para montar a linha do tempo no período.</p>
              <p className="text-xs text-gray-400 mt-1">
                Não é zero: é ausência de viagem registrada. Veja as observações abaixo.
              </p>
            </div>
          ) : shown.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Nenhum trecho deste tipo no período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-gray-400 text-xs">
                    <th className="px-5 py-2 text-left font-medium">#</th>
                    <th className="px-3 py-2 text-left font-medium">Trecho</th>
                    <th className="px-3 py-2 text-left font-medium">Rota</th>
                    <th className="px-3 py-2 text-left font-medium">Período</th>
                    <th className="px-3 py-2 text-right font-medium">Apurado</th>
                    <th className="px-3 py-2 text-right font-medium">Estimado</th>
                    <th className="px-5 py-2 text-right font-medium">Receita</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shown.map((s) => {
                    const style = TYPE_STYLE[s.type];
                    return (
                      <tr key={s.sequence} className="text-gray-700 align-top hover:bg-gray-50">
                        <td className="px-5 py-2.5 text-gray-400">{s.sequence}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                            <span className={style.text}>{s.typeLabel}</span>
                          </span>
                          {s.fromPreviousPeriod && (
                            <span className="text-xs text-gray-400 ml-4">viagem anterior ao período</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="whitespace-nowrap">
                            {s.origin ?? <span className="text-gray-400 italic">sem cidade</span>}
                            <span className="text-gray-300 mx-1.5">→</span>
                            {s.destination ?? <span className="text-gray-400 italic">sem cidade</span>}
                          </span>
                          {s.warnings.map((w, i) => (
                            <span key={i} className="flex items-center gap-1.5 text-xs text-amber-700 mt-1">
                              <IconAlertTriangle className="w-3 h-3 shrink-0" />
                              {w}
                            </span>
                          ))}
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                          {s.departureAt ? fmtDateTime(s.departureAt) : "—"}
                          {s.arrivalAt && <span className="text-gray-300"> → </span>}
                          {s.arrivalAt ? fmtDateTime(s.arrivalAt) : ""}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium whitespace-nowrap">{km(s.actualDistanceKm)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-500 whitespace-nowrap">
                          {s.plannedDistanceKm != null ? km(s.plannedDistanceKm)
                            : <span className="text-gray-300" title="sem motor de rotas configurado">—</span>}
                        </td>
                        <td className="px-5 py-2.5 text-right whitespace-nowrap">
                          {/* Trecho vazio não fatura: mostrar R$ 0,00 sugeriria frete perdido. */}
                          {s.loaded ? fmtBRL(s.revenue) : <span className="text-gray-300">sem carga</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data.limitations.length > 0 && (
            <div className="px-5 py-3 border-t border-surface-border text-xs text-gray-500 space-y-1">
              <p className="font-medium text-gray-600">O que esta apuração não sabe</p>
              {data.limitations.map((l, i) => <p key={i}>• {l}</p>)}
            </div>
          )}

          {/* Crédito do provedor de rotas — exigido pelos termos de uso do ORS e do
              OpenStreetMap. Só aparece quando a rota estimada veio de lá. */}
          {data.attribution && (
            <p className="px-5 py-2 border-t border-surface-border text-[11px] text-gray-400">
              {data.attribution}
            </p>
          )}
        </>
      )}
    </div>
  );
}
